package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.data.dto.AdminRestaurantDto
import com.qrkontaq.data.dto.DayHoursDto
import com.qrkontaq.data.dto.UpdateRestaurantRequestDto
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.components.forms.TextInput
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

private val DAY_KEYS = listOf("mon", "tue", "wed", "thu", "fri", "sat", "sun")

/** Guest-site languages; at least one must stay selected (English by default). */
internal val SITE_LANGUAGE_OPTIONS =
    listOf(
        "en" to "English", "uk" to "Українська", "pl" to "Polski", "ru" to "Русский",
        "de" to "Deutsch", "es" to "Español", "fr" to "Français",
    )

/** Curated ISO-4217 currencies for the menu; second value is the display symbol ("" = none). */
internal val CURRENCY_OPTIONS =
    listOf(
        "EUR" to "€", "USD" to "$", "UAH" to "₴", "PLN" to "zł",
        "GBP" to "£", "CZK" to "Kč", "RON" to "lei", "MDL" to "",
        "GEL" to "₾", "KZT" to "₸", "TRY" to "₺", "AED" to "",
    )

private fun days() =
    listOf(
        "mon" to AdminT.dayMon, "tue" to AdminT.dayTue, "wed" to AdminT.dayWed, "thu" to AdminT.dayThu,
        "fri" to AdminT.dayFri, "sat" to AdminT.daySat, "sun" to AdminT.daySun,
    )

/**
 * Public entry: the [restaurant] handed down from AdminShell is cached at page boot and
 * can be stale (e.g. after saving here and re-opening the tab). Refetch fresh data so the
 * form always seeds from what's actually stored, then render the form keyed on that data.
 */
@Composable
fun SettingsTab(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    restaurant: AdminRestaurantDto,
    role: String,
    onDeleted: () -> Unit,
    onUpgrade: () -> Unit,
) {
    var fresh by remember(rid) { mutableStateOf<AdminRestaurantDto?>(null) }
    LaunchedEffect(rid) {
        fresh = runCatching { repository.getRestaurant(rid) }.getOrNull() ?: restaurant
    }
    val data = fresh
    if (data == null) {
        Box(
            Modifier.fillMaxWidth().height(220.px).borderRadius(16.px)
                .attrsModifier { classes("qk-skeleton") },
        )
        return
    }
    SettingsTabContent(repository, scope, rid, data, role, onDeleted, onUpgrade)
}

@Composable
private fun SettingsTabContent(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    restaurant: AdminRestaurantDto,
    role: String,
    onDeleted: () -> Unit,
    onUpgrade: () -> Unit,
) {
    var name by remember(rid) { mutableStateOf(restaurant.name) }
    var descriptions by remember(rid) { mutableStateOf(restaurant.description) }
    var phone by remember(rid) { mutableStateOf(restaurant.phone.orEmpty()) }
    var address by remember(rid) { mutableStateOf(restaurant.address.orEmpty()) }
    var lat by remember(rid) { mutableStateOf(restaurant.lat) }
    var lng by remember(rid) { mutableStateOf(restaurant.lng) }
    var facebook by remember(rid) { mutableStateOf(restaurant.socials["facebook"].orEmpty()) }
    var instagram by remember(rid) { mutableStateOf(restaurant.socials["instagram"].orEmpty()) }
    var website by remember(rid) { mutableStateOf(restaurant.socials["website"].orEmpty()) }
    var accentColor by remember(rid) { mutableStateOf(restaurant.accentColor.orEmpty()) }
    var logoUrl by remember(rid) { mutableStateOf(restaurant.logoUrl) }
    var coverUrl by remember(rid) { mutableStateOf(restaurant.coverUrl) }
    var hours by remember(rid) {
        mutableStateOf(
            DAY_KEYS.associateWith { key ->
                restaurant.workingHours[key]?.let { it.open to it.close }
            },
        )
    }
    var siteLangs by remember(rid) {
        mutableStateOf(restaurant.languages.toSet().takeIf { it.isNotEmpty() } ?: setOf("en"))
    }
    var theme by remember(rid) {
        mutableStateOf(restaurant.theme.takeIf { it == "auto" || it == "dark" } ?: "light")
    }
    var categoryThumbs by remember(rid) { mutableStateOf(restaurant.menuCategoryThumbs) }
    var featureFirst by remember(rid) { mutableStateOf(restaurant.menuFeatureFirst) }
    var showPopular by remember(rid) { mutableStateOf(restaurant.menuShowPopular) }
    var showChef by remember(rid) { mutableStateOf(restaurant.menuShowChef) }
    var showFilters by remember(rid) { mutableStateOf(restaurant.menuShowFilters) }
    var currency by remember(rid) { mutableStateOf(restaurant.currency) }
    var guestCheckEnabled by remember(rid) { mutableStateOf(restaurant.guestCheckEnabled) }
    var waiterCallEnabled by remember(rid) { mutableStateOf(restaurant.waiterCallEnabled) }
    var loyaltyEnabled by remember(rid) { mutableStateOf(restaurant.loyaltyEnabled) }
    var loyaltyRate by remember(rid) { mutableStateOf(restaurant.loyaltyRatePercent.toString()) }
    var error by remember { mutableStateOf<String?>(null) }
    val toast = com.qrkontaq.ui.components.notifications.LocalToastState.current

    // ── Brand ────────────────────────────────────────────────────────────────
    SettingsSection(AdminIcons.Palette, AdminT.brandTitle, tintHex = "#C4573A") {
        BrandCover(coverUrl, repository, scope, rid) { coverUrl = it }
        Row(Modifier.fillMaxWidth().margin(bottom = 4.px)) {
            Column {
                FieldLabel(AdminT.logoLabel)
                BrandLogoTile(logoUrl, repository, scope, rid) { logoUrl = it }
            }
            Box(Modifier.fillMaxWidth().margin(left = 12.px)) {
                TextInput(value = name, onValueChange = { name = it }, label = AdminT.nameLabel)
            }
        }
        SITE_LANGUAGE_OPTIONS.map { it.first }.filter { it in siteLangs }.forEach { code ->
            TextInput(
                value = descriptions[code].orEmpty(),
                onValueChange = { descriptions = descriptions + (code to it) },
                label = "${AdminT.shortDescription} (${code.uppercase()})",
            )
        }

        AdminHint(AdminT.accentHint)
        val presets = listOf(
            "#C4573A", "#0F9D6E", "#B0413E", "#C98A2B",
            "#4A6FA5", "#7A4A9E", "#2E6E5E", "#1F2421",
        )
        val isPreset = presets.any { accentColor.equals(it, ignoreCase = true) }
        val hasCustom = accentColor.isNotBlank() && !isPreset
        Row(
            Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(8.px).margin(top = 4.px, bottom = 4.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            presets.forEach { preset ->
                val selected = accentColor.equals(preset, ignoreCase = true)
                Box(
                    Modifier
                        .size(32.px)
                        .borderRadius(999.px)
                        .backgroundColor(Brand.accentFrom(preset))
                        .cursor(Cursor.Pointer)
                        .then(
                            if (selected) {
                                Modifier.border(3.px, LineStyle.Solid, Brand.Surface)
                                    .boxShadow(spreadRadius = 2.px, color = Brand.Charcoal)
                            } else {
                                Modifier
                            },
                        )
                        .attrsModifier { classes("qk-swatch", "qk-press"); attr("role", "button"); attr("aria-label", preset) }
                        .onClick { accentColor = preset },
                )
            }

            // Custom colour — click opens the native colour-picker popup.
            Box(
                Modifier
                    .size(32.px)
                    .borderRadius(999.px)
                    .position(Position.Relative)
                    .cursor(Cursor.Pointer)
                    .then(
                        if (hasCustom) {
                            Modifier.backgroundColor(Brand.accentFrom(accentColor))
                                .border(3.px, LineStyle.Solid, Brand.Surface)
                                .boxShadow(spreadRadius = 2.px, color = Brand.Charcoal)
                        } else {
                            Modifier.styleModifier {
                                property(
                                    "background",
                                    "conic-gradient(from 90deg, #F87171, #FBBF24, #34D399, #60A5FA, #A78BFA, #F87171)",
                                )
                            }
                        },
                    )
                    .attrsModifier { classes("qk-swatch"); attr("role", "button"); attr("aria-label", AdminT.customColorLabel) },
                contentAlignment = Alignment.Center,
            ) {
                if (!hasCustom) {
                    Box(
                        Modifier.size(18.px).minWidth(18.px).borderRadius(999.px).backgroundColor(Brand.Surface),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineIcon(AdminIcons.Plus, 12, "#1F2421")
                    }
                }
                Input(type = InputType.Color) {
                    classes("qk-color-input")
                    value(accentColor.takeIf { it.matches(Regex("^#[0-9a-fA-F]{6}$")) } ?: Brand.DefaultAccentHex)
                    onInput { accentColor = it.value }
                }
            }
        }
    }

    // ── Contacts ─────────────────────────────────────────────────────────────
    SettingsSection(AdminIcons.MapPin, AdminT.contactsTitle, tintHex = "#4A6FA5") {
        TextInput(value = address, onValueChange = { address = it }, label = AdminT.addressLabel)
        MapPicker(lat = lat, lng = lng) { la, ln ->
            lat = la
            lng = ln
        }
        TextInput(value = phone, onValueChange = { phone = it }, label = AdminT.phoneLabel, placeholder = "+380…")
        AdminHint(AdminT.socialsHint)
        Row(Modifier.fillMaxWidth()) {
            Box(Modifier.fillMaxWidth().margin(right = 6.px)) {
                TextInput(value = facebook, onValueChange = { facebook = it }, label = "Facebook", placeholder = "https://facebook.com/…")
            }
            Box(Modifier.fillMaxWidth()) {
                TextInput(value = instagram, onValueChange = { instagram = it }, label = "Instagram", placeholder = "https://instagram.com/…")
            }
        }
        TextInput(value = website, onValueChange = { website = it }, label = "Website", placeholder = "https://…")
    }

    // ── Opening hours ────────────────────────────────────────────────────────
    SettingsSection(AdminIcons.Clock, AdminT.workingHoursTitle, subtitle = AdminT.workingHoursHint, tintHex = "#C98A2B") {
        days().forEach { (key, label) ->
            val current = hours[key]
            val off = current == null
            Row(
                Modifier.fillMaxWidth().margin(bottom = 8.px)
                    .backgroundColor(if (off) Brand.WarmBackground else Brand.Surface)
                    .borderRadius(12.px)
                    .border(1.px, LineStyle.Solid, if (off) Color.rgba(31, 36, 33, 0.04f) else Brand.Divider)
                    .padding(topBottom = 8.px, leftRight = 12.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                AdminToggle(checked = !off) { on ->
                    hours = hours + (
                        key to if (on) {
                            (current?.first ?: "09:00") to (current?.second ?: "22:00")
                        } else {
                            null
                        }
                    )
                }
                SpanText(
                    label,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold)
                        .color(if (off) Brand.TextSecondary else Brand.Charcoal)
                        .minWidth(42.px).margin(left = 10.px),
                )
                Box(Modifier.fillMaxWidth())
                if (current != null) {
                    TimeField(current.first) { newOpen ->
                        hours = hours + (key to (newOpen to current.second))
                    }
                    SpanText("–", Modifier.fontSize(15.px).color(Brand.TextSecondary).margin(leftRight = 8.px))
                    TimeField(current.second) { newClose ->
                        hours = hours + (key to (current.first to newClose))
                    }
                } else {
                    SpanText(
                        AdminT.dayOff,
                        Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).color(Brand.TextSecondary),
                    )
                }
            }
        }
    }

    // ── Site theme ───────────────────────────────────────────────────────────
    SettingsSection(AdminIcons.Sparkles, AdminT.siteThemeTitle, subtitle = AdminT.siteThemeHint, tintHex = "#7A4A9E") {
        Row(Modifier.margin(top = 6.px).flexWrap(FlexWrap.Wrap).gap(8.px)) {
            listOf(
                "light" to AdminT.themeLight,
                "auto" to AdminT.themeAuto,
                "dark" to AdminT.themeDark,
            ).forEach { (value, label) ->
                SettingsChip(label, theme == value) { theme = value }
            }
        }
    }

    // ── Menu appearance ──────────────────────────────────────────────────────
    SettingsSection(AdminIcons.LayoutGrid, AdminT.menuLookTitle, tintHex = "#0F9D6E") {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 12.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = categoryThumbs) { categoryThumbs = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(
                    AdminT.categoryPhotosTitle,
                    Modifier.fontSize(14.px).color(Brand.Charcoal),
                )
                AdminHint(AdminT.categoryPhotosHint)
            }
        }
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = featureFirst) { featureFirst = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(
                    AdminT.featureFirstTitle,
                    Modifier.fontSize(14.px).color(Brand.Charcoal),
                )
                AdminHint(AdminT.featureFirstHint)
            }
        }
        // "Popular" and "Chef recommends" sell dishes — that's a Pro perk.
        val proMenu = restaurant.isPro
        Row(
            Modifier.fillMaxWidth().margin(top = 12.px).opacity(if (proMenu) 1.0 else 0.5),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = showPopular && proMenu) { if (proMenu) showPopular = it }
            Column(Modifier.margin(left = 10.px)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        AdminT.showPopularTitle,
                        Modifier.fontSize(14.px).color(Brand.Charcoal),
                    )
                    if (!proMenu) ProBadge(onUpgrade)
                }
                AdminHint(AdminT.showPopularHint)
            }
        }
        Row(
            Modifier.fillMaxWidth().margin(top = 12.px).opacity(if (proMenu) 1.0 else 0.5),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = showChef && proMenu) { if (proMenu) showChef = it }
            Column(Modifier.margin(left = 10.px)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        AdminT.showChefTitle,
                        Modifier.fontSize(14.px).color(Brand.Charcoal),
                    )
                    if (!proMenu) ProBadge(onUpgrade)
                }
                AdminHint(AdminT.showChefHint)
            }
        }
        Row(
            Modifier.fillMaxWidth().margin(top = 12.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = showFilters) { showFilters = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(
                    AdminT.showFiltersTitle,
                    Modifier.fontSize(14.px).color(Brand.Charcoal),
                )
                AdminHint(AdminT.showFiltersHint)
            }
        }
    }

    // ── Menu languages ───────────────────────────────────────────────────────
    SettingsSection(AdminIcons.Globe, AdminT.menuLanguagesTitle, subtitle = AdminT.menuLanguagesHint, tintHex = "#2E6E5E") {
        Row(Modifier.margin(top = 6.px).flexWrap(FlexWrap.Wrap).gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            SITE_LANGUAGE_OPTIONS.forEach { (code, label) ->
                val selected = code in siteLangs
                // On Free every non-active language wears a lock: it exists, Pro
                // activates them all; tapping still just moves the single slot.
                SettingsChip(label, selected, locked = !restaurant.isPro && !selected) {
                    siteLangs = when {
                        !restaurant.isPro -> setOf(code)
                        !selected -> siteLangs + code
                        siteLangs.size > 1 -> siteLangs - code
                        else -> siteLangs // the last language cannot be unselected
                    }
                }
            }
        }
        if (!restaurant.isPro) {
            Box(Modifier.fillMaxWidth().margin(top = 14.px)) {
                ProLockNote(body = AdminT.proLangHint, onUpgrade = onUpgrade)
            }
        }
    }

    // ── Currency ─────────────────────────────────────────────────────────────
    SettingsSection(AdminIcons.Wallet, AdminT.currencyTitle, subtitle = AdminT.currencyHint, tintHex = "#B0413E") {
        Row(Modifier.margin(top = 6.px).flexWrap(FlexWrap.Wrap).gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            CURRENCY_OPTIONS.forEach { (code, symbol) ->
                SettingsChip(if (symbol.isNotEmpty()) "$code — $symbol" else code, code == currency) { currency = code }
            }
        }
    }

    // ── Table services (guest QR page) — Pro-only ────────────────────────────
    val isProVenue = restaurant.isPro
    SettingsSection(
        AdminIcons.QrCode,
        AdminT.guestServicesTitle,
        subtitle = AdminT.guestServicesHint,
        tintHex = "#0F9D6E",
    ) {
        if (!isProVenue) ProLockNote(onUpgrade = onUpgrade)
        Row(
            Modifier.fillMaxWidth().margin(bottom = 12.px).opacity(if (isProVenue) 1.0 else 0.5),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = guestCheckEnabled && isProVenue) { if (isProVenue) guestCheckEnabled = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(AdminT.guestCheckTitle, Modifier.fontSize(14.px).color(Brand.Charcoal))
                AdminHint(AdminT.guestCheckHint)
            }
        }
        Row(
            Modifier.fillMaxWidth().opacity(if (isProVenue) 1.0 else 0.5),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = waiterCallEnabled && isProVenue) { if (isProVenue) waiterCallEnabled = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(AdminT.waiterCallTitle, Modifier.fontSize(14.px).color(Brand.Charcoal))
                AdminHint(AdminT.waiterCallHint)
            }
        }
    }

    // ── Loyalty — Pro-only ───────────────────────────────────────────────────
    SettingsSection(AdminIcons.Gift, AdminT.loyaltyTitle, tintHex = "#C98A2B") {
        if (!isProVenue) ProLockNote(onUpgrade = onUpgrade)
        Row(
            Modifier.fillMaxWidth().margin(bottom = 12.px).opacity(if (isProVenue) 1.0 else 0.5),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AdminToggle(checked = loyaltyEnabled && isProVenue) { if (isProVenue) loyaltyEnabled = it }
            Column(Modifier.margin(left = 10.px)) {
                SpanText(
                    AdminT.loyaltyEnableTitle,
                    Modifier.fontSize(14.px).color(Brand.Charcoal),
                )
                AdminHint(AdminT.loyaltyEnableHint)
            }
        }
        if (loyaltyEnabled && isProVenue) {
            Box(Modifier.width(220.px)) {
                TextInput(
                    value = loyaltyRate,
                    onValueChange = { input -> loyaltyRate = input.filter { it.isDigit() }.take(2) },
                    label = AdminT.loyaltyRateLabel,
                    placeholder = "5",
                )
            }
            AdminHint(AdminT.loyaltyRateHint)
        }
    }

    // ── Sticky save bar ──────────────────────────────────────────────────────
    Column(
        Modifier.fillMaxWidth().margin(top = 8.px, bottom = 10.px)
            .zIndex(20)
            .styleModifier {
                property("position", "sticky")
                property("bottom", "12px")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val status = when {
            !error.isNullOrBlank() -> Triple(AdminIcons.Ban, Brand.CoralText, error!!)
            else -> null
        }
        status?.let { (icon, tint, text) ->
            Row(
                Modifier.margin(bottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(Modifier.margin(right = 8.px)) { InlineIcon(icon, 16, if (tint == Brand.Green) "#2E9E5B" else "#E05B4C") }
                SpanText(
                    text,
                    Modifier.fontSize(13.px).color(tint).attrsModifier { classes("qk-clamp1") },
                )
            }
        }
        AdminButton(
            text = AdminT.saveChanges,
            modifier = Modifier.minWidth(220.px)
                .boxShadow(offsetY = 6.px, blurRadius = 18.px, color = Color.rgba(15, 157, 110, 0.35f)),
            onClick = {
                error = null
                scope.launch {
                    val result =
                        runCatching {
                            repository.updateRestaurant(
                                rid,
                                UpdateRestaurantRequestDto(
                                    name = name.takeIf { it.isNotBlank() },
                                    description =
                                        descriptions.mapValues { it.value.trim() }
                                            .filterValues { it.isNotBlank() },
                                    logoUrl = logoUrl,
                                    coverUrl = coverUrl,
                                    accentColor = accentColor.takeIf { it.isNotBlank() },
                                    address = address.takeIf { it.isNotBlank() },
                                    lat = lat,
                                    lng = lng,
                                    phone = phone.takeIf { it.isNotBlank() },
                                    socials = buildMap {
                                        facebook.trim().takeIf { it.isNotBlank() }?.let { put("facebook", it) }
                                        instagram.trim().takeIf { it.isNotBlank() }?.let { put("instagram", it) }
                                        website.trim().takeIf { it.isNotBlank() }?.let { put("website", it) }
                                    },
                                    workingHours =
                                        hours.mapValues { (_, value) ->
                                            value?.takeIf { it.first.isNotBlank() && it.second.isNotBlank() }
                                                ?.let { DayHoursDto(open = it.first, close = it.second) }
                                        },
                                    languages =
                                        SITE_LANGUAGE_OPTIONS.map { it.first }.filter { it in siteLangs },
                                    defaultLanguage =
                                        SITE_LANGUAGE_OPTIONS.map { it.first }.first { it in siteLangs },
                                    theme = theme,
                                    menuCategoryThumbs = categoryThumbs,
                                    menuFeatureFirst = featureFirst,
                                    menuShowPopular = showPopular,
                                    menuShowChef = showChef,
                                    menuShowFilters = showFilters,
                                    currency = currency,
                                    loyaltyEnabled = loyaltyEnabled,
                                    loyaltyRatePercent = loyaltyRate.toIntOrNull()?.coerceIn(1, 50),
                                    guestCheckEnabled = guestCheckEnabled,
                                    waiterCallEnabled = waiterCallEnabled,
                                ),
                            )
                        }.getOrNull()
                    if (result != null) {
                        toast.success(AdminT.savedSiteUpdated)
                    } else {
                        error = AdminT.saveFailedCheckColor
                        toast.error(AdminT.saveFailed)
                    }
                }
            },
        )
    }

    // ── Danger zone (owner only) ─────────────────────────────────────────────
    if (role == "OWNER") {
        DangerZone(repository, scope, rid, restaurant.name, onDeleted)
    }
}

/**
 * Owner-only "delete this venue" card. Deletion wipes everything tied to the venue —
 * menu, photos, tables & QR codes, orders, team, loyalty and the site address — so it
 * hides behind a modal that requires typing the venue name exactly before it enables.
 */
@Composable
private fun DangerZone(
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    venueName: String,
    onDeleted: () -> Unit,
) {
    var confirmOpen by remember(rid) { mutableStateOf(false) }
    var typed by remember(rid) { mutableStateOf("") }
    var deleting by remember(rid) { mutableStateOf(false) }
    var failed by remember(rid) { mutableStateOf(false) }
    val toast = com.qrkontaq.ui.components.notifications.LocalToastState.current

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Color.rgba(224, 91, 76, 0.05f))
            .borderRadius(18.px)
            .border(1.px, LineStyle.Solid, Color.rgba(224, 91, 76, 0.28f))
            .padding(20.px)
            .margin(bottom = 14.px),
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 12.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                Modifier.size(42.px).minWidth(42.px).borderRadius(13.px).margin(right = 12.px)
                    .styleModifier {
                        property("background", "linear-gradient(140deg, #E05B4C26, #E05B4C0D)")
                        property("border", "1px solid #E05B4C2E")
                    },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(AdminIcons.Trash, 20, "#C4433A")
            }
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    AdminT.dangerZoneTitle,
                    Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                )
                SpanText(
                    AdminT.dangerZoneHint,
                    Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 3.px),
                )
            }
        }
        AdminButton(
            text = AdminT.deleteVenueButton,
            danger = true,
            iconSvg = AdminIcons.Trash,
            modifier = Modifier.minWidth(200.px),
            onClick = {
                typed = ""
                failed = false
                confirmOpen = true
            },
        )
    }

    if (confirmOpen) {
        val confirmed = typed.trim().equals(venueName.trim(), ignoreCase = false)
        // Dim backdrop — a click outside cancels (unless a delete is already running).
        Box(
            Modifier
                .position(Position.Fixed).top(0.px).left(0.px).right(0.px).bottom(0.px)
                .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
                .zIndex(60)
                .attrsModifier { classes("qk-fade-in") }
                .onClick { if (!deleting) confirmOpen = false },
        )
        Box(
            Modifier
                .position(Position.Fixed).top(0.px).left(0.px).right(0.px).bottom(0.px)
                .zIndex(61)
                .padding(20.px)
                .styleModifier { property("pointer-events", "none") },
            contentAlignment = Alignment.Center,
        ) {
            Column(
                Modifier
                    .fillMaxWidth().maxWidth(440.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(20.px)
                    .padding(24.px)
                    .boxShadow(offsetY = 20.px, blurRadius = 60.px, color = Color.rgba(15, 20, 17, 0.28f))
                    .styleModifier { property("pointer-events", "auto") }
                    .attrsModifier { classes("qk-fade-in") },
            ) {
                Row(Modifier.fillMaxWidth().margin(bottom = 12.px), verticalAlignment = Alignment.CenterVertically) {
                    Box(
                        Modifier.size(40.px).minWidth(40.px).borderRadius(12.px).margin(right = 12.px)
                            .backgroundColor(Color.rgba(224, 91, 76, 0.12f)),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineIcon(AdminIcons.Trash, 19, "#C4433A")
                    }
                    SpanText(
                        AdminT.deleteVenueModalTitle,
                        Modifier.fontSize(18.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                    )
                }
                SpanText(
                    AdminT.deleteVenueModalBody,
                    Modifier.fontSize(13.px).lineHeight(1.5).color(Brand.TextSecondary).margin(bottom = 16.px),
                )
                SpanText(
                    AdminT.deleteVenueConfirmLabel(venueName),
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal).margin(bottom = 6.px),
                )
                TextInput(
                    value = typed,
                    onValueChange = { typed = it },
                    label = null,
                    placeholder = AdminT.deleteVenueConfirmPlaceholder,
                )
                if (failed) {
                    Row(Modifier.margin(top = 10.px), verticalAlignment = Alignment.CenterVertically) {
                        Box(Modifier.margin(right = 6.px)) { InlineIcon(AdminIcons.Ban, 15, "#E05B4C") }
                        SpanText(AdminT.deleteFailed, Modifier.fontSize(12.px).color(Brand.CoralText))
                    }
                }
                Row(
                    Modifier.fillMaxWidth().margin(top = 18.px).gap(10.px),
                    horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.End,
                ) {
                    AdminButton(
                        text = AdminT.cancel,
                        secondary = true,
                        enabled = !deleting,
                        onClick = { confirmOpen = false },
                    )
                    AdminButton(
                        text = AdminT.deleteVenueConfirmCta,
                        danger = true,
                        enabled = confirmed && !deleting,
                        onClick = {
                            if (!confirmed || deleting) return@AdminButton
                            failed = false
                            deleting = true
                            scope.launch {
                                val ok =
                                    runCatching { repository.deleteRestaurant(rid) }
                                        .getOrNull()?.let { it.code in 200..299 } == true
                                deleting = false
                                if (ok) {
                                    confirmOpen = false
                                    toast.success(AdminT.deleteVenueSuccess)
                                    onDeleted()
                                } else {
                                    failed = true
                                    toast.error(AdminT.deleteFailed)
                                }
                            }
                        },
                    )
                }
            }
        }
    }
}

/** Card section with a tinted, glossy icon badge and a title/subtitle header. */
@Composable
private fun SettingsSection(
    icon: String,
    title: String,
    subtitle: String? = null,
    tintHex: String = "#0F9D6E",
    content: @Composable () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Brand.Surface)
            .borderRadius(18.px)
            .border(1.px, LineStyle.Solid, Color.rgba(31, 36, 33, 0.06f))
            .padding(20.px)
            .margin(bottom = 14.px)
            .boxShadow(offsetY = 2.px, blurRadius = 10.px, color = Color.rgba(31, 36, 33, 0.04f))
            .attrsModifier { classes("qk-set-card") },
    ) {
        Row(
            Modifier.fillMaxWidth().margin(bottom = 16.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                Modifier
                    .size(42.px)
                    .minWidth(42.px)
                    .borderRadius(13.px)
                    .margin(right = 12.px)
                    .styleModifier {
                        property("background", "linear-gradient(140deg, ${tintHex}26, ${tintHex}0D)")
                        property("border", "1px solid ${tintHex}2E")
                    }
                    .attrsModifier { classes("qk-set-badge") },
                contentAlignment = Alignment.Center,
            ) {
                InlineIcon(icon, 20, tintHex)
            }
            Column(Modifier.fillMaxWidth()) {
                SpanText(
                    title,
                    Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                )
                subtitle?.let {
                    SpanText(
                        it,
                        Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 3.px),
                    )
                }
            }
        }
        content()
    }
}

/** Small clickable "Pro" pill next to a locked control; jumps to the subscription tab. */
@Composable
private fun ProBadge(onUpgrade: () -> Unit) {
    Box(
        Modifier
            .borderRadius(999.px)
            .padding(topBottom = 2.px, leftRight = 8.px)
            .margin(left = 8.px)
            .cursor(Cursor.Pointer)
            .backgroundColor(Color.rgb(0xC9, 0x8A, 0x2B))
            .onClick { onUpgrade() },
    ) {
        SpanText(
            "Pro",
            Modifier.fontSize(11.px).fontWeight(FontWeight.Bold).color(Brand.Surface),
        )
    }
}

/**
 * Upsell card in Pro-only sections on the Free plan: lock badge, title/body and
 * an emerald CTA leading to the subscription tab.
 */
@Composable
private fun ProLockNote(
    body: String = AdminT.proSectionHint,
    onUpgrade: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .borderRadius(14.px)
            .padding(14.px)
            .margin(bottom = 14.px)
            .flexWrap(FlexWrap.Wrap)
            .gap(10.px)
            .styleModifier {
                property("background", "linear-gradient(135deg, #C98A2B1A, #C98A2B08)")
                property("border", "1px solid #C98A2B33")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(36.px)
                .minWidth(36.px)
                .borderRadius(11.px)
                .styleModifier {
                    property("background", "linear-gradient(135deg, #C98A2B, #A96F1B)")
                    property("box-shadow", "0 8px 18px -8px rgba(201,138,43,.7)")
                },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(AdminIcons.Lock, 17, "#FFFFFF")
        }
        Column(
            Modifier.styleModifier {
                property("flex", "1 1 220px")
                property("min-width", "0")
            },
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    AdminT.proLockTitle,
                    Modifier.fontSize(13.5.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal),
                )
                Box(
                    Modifier
                        .borderRadius(999.px)
                        .padding(topBottom = 1.px, leftRight = 7.px)
                        .margin(left = 7.px)
                        .backgroundColor(Color.rgb(0xC9, 0x8A, 0x2B)),
                ) {
                    SpanText(
                        "Pro",
                        Modifier.fontSize(10.px).fontWeight(FontWeight.Bold).color(Brand.Surface),
                    )
                }
            }
            SpanText(
                body,
                Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 2.px),
            )
        }
        Box(
            Modifier
                .borderRadius(999.px)
                .padding(topBottom = 8.px, leftRight = 16.px)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("background", "linear-gradient(135deg, #17B981, #0F9D6E)")
                    property("box-shadow", "0 10px 22px -10px rgba(15,157,110,.6)")
                    property("flex-shrink", "0")
                }
                .onClick { onUpgrade() },
        ) {
            SpanText(
                AdminT.proUpgradeCta,
                Modifier.fontSize(12.5.px).fontWeight(FontWeight.Bold).color(Brand.Surface),
            )
        }
    }
}

/** Rounded selectable pill used by theme / language / currency pickers; [locked] adds a Pro padlock. */
@Composable
private fun SettingsChip(
    label: String,
    selected: Boolean,
    locked: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .borderRadius(999.px)
            .padding(topBottom = 8.px, leftRight = 15.px)
            .cursor(Cursor.Pointer)
            .flexShrink(0)
            .then(
                if (selected) {
                    Modifier
                        .styleModifier { property("background", "linear-gradient(135deg, #17B981, #0F9D6E)") }
                        .boxShadow(offsetY = 3.px, blurRadius = 10.px, color = Color.rgba(15, 157, 110, 0.32f))
                } else {
                    Modifier
                        .backgroundColor(Brand.WarmBackground)
                        .border(1.px, LineStyle.Solid, Brand.Divider)
                },
            )
            .attrsModifier { classes("qk-set-chip", "qk-press"); attr("role", "button") }
            .onClick { onClick() },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (locked) {
                Box(Modifier.margin(right = 6.px).opacity(0.75)) {
                    InlineIcon(AdminIcons.Lock, 12, "#C98A2B")
                }
            }
            SpanText(
                label,
                Modifier
                    .fontSize(13.px)
                    .fontWeight(if (selected) FontWeight.SemiBold else FontWeight.Medium)
                    .color(if (selected) Brand.Surface else Brand.TextSecondary)
                    .attrsModifier { classes("qk-nowrap") },
            )
        }
    }
}

/** Native time picker — only valid HH:MM can be entered (no free-text / letters). */
@Composable
private fun TimeField(value: String, onChange: (String) -> Unit) {
    Input(type = InputType.Time) {
        value(value)
        onInput { onChange(it.value) }
        style {
            property("width", "116px")
            property("padding", "9px 10px")
            property("font-size", "15px")
            property("font-family", "inherit")
            property("box-sizing", "border-box")
            property("color-scheme", "light")
        }
    }
}

/** Small field label matching the TextInput label style. */
@Composable
private fun FieldLabel(text: String) {
    SpanText(
        text,
        Modifier.fontSize(14.px).color(Brand.TextSecondary).margin(bottom = 4.px),
    )
}

/** Compact, clean cover-image picker: a wide banner with a corner "+" / replace button. */
@Composable
private fun BrandCover(
    coverUrl: String?,
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    onUploaded: (String) -> Unit,
) {
    var uploading by remember { mutableStateOf(false) }
    FieldLabel(AdminT.coverLabel)
    Box(
        Modifier
            .fillMaxWidth()
            .height(140.px)
            .borderRadius(12.px)
            .margin(bottom = 12.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .styleModifier { property("overflow", "hidden") }
            .then(
                if (coverUrl == null) {
                    Modifier.backgroundColor(Brand.WarmBackground).border(1.px, LineStyle.Solid, Brand.Divider)
                } else {
                    Modifier
                },
            ),
        contentAlignment = Alignment.Center,
    ) {
        coverUrl?.let { cover ->
            Image(src = cover, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
        } ?: SpanText(
            AdminT.upload,
            Modifier.fontSize(13.px).fontWeight(FontWeight.Medium).color(Brand.TextSecondary)
                .styleModifier { property("pointer-events", "none") },
        )
        CornerButton(coverUrl == null)
        if (uploading) UploadSpinner() else UploadOverlay { file ->
            uploading = true
            scope.launch {
                val url = runCatching { repository.uploadMedia(rid, "cover", file) }.getOrNull()
                uploading = false
                url?.let(onUploaded)
            }
        }
    }
}

/** Compact square logo picker with a "+" placeholder / replace corner. */
@Composable
private fun BrandLogoTile(
    logoUrl: String?,
    repository: AdminRepository,
    scope: CoroutineScope,
    rid: String,
    onUploaded: (String) -> Unit,
) {
    var uploading by remember { mutableStateOf(false) }
    Box(
        Modifier
            .size(64.px).minWidth(64.px)
            .borderRadius(12.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .styleModifier { property("overflow", "hidden") }
            .then(
                if (logoUrl == null) {
                    Modifier.backgroundColor(Brand.WarmBackground).border(1.px, LineStyle.Solid, Brand.Divider)
                } else {
                    Modifier.border(1.px, LineStyle.Solid, Brand.Divider)
                },
            ),
        contentAlignment = Alignment.Center,
    ) {
        logoUrl?.let { logo ->
            Image(src = logo, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
            CornerButton(false)
        } ?: InlineIcon(AdminIcons.Plus, 22, "#0F9D6E")
        if (uploading) UploadSpinner() else UploadOverlay { file ->
            uploading = true
            scope.launch {
                val url = runCatching { repository.uploadMedia(rid, "logo", file) }.getOrNull()
                uploading = false
                url?.let(onUploaded)
            }
        }
    }
}

/** Small round action pill in the top-right corner of a media zone (decorative — the whole zone is clickable). */
@Composable
private fun CornerButton(empty: Boolean) {
    Box(
        Modifier
            .position(Position.Absolute).top(8.px).right(8.px)
            .size(28.px).minWidth(28.px).borderRadius(999.px)
            .styleModifier { property("pointer-events", "none") }
            .then(
                if (empty) {
                    Modifier.backgroundColor(Brand.Surface)
                        .boxShadow(offsetY = 2.px, blurRadius = 6.px, color = Color.rgba(31, 36, 33, 0.12f))
                } else {
                    Modifier.backgroundColor(Color.rgba(0, 0, 0, 0.5f))
                },
            ),
        contentAlignment = Alignment.Center,
    ) {
        InlineIcon(if (empty) AdminIcons.Plus else AdminIcons.ImagePlus, 15, if (empty) "#0F9D6E" else "#FFFFFF")
    }
}

/** Transparent file input filling its parent; triggers the OS file dialog on click. */
@Composable
private fun UploadOverlay(onFile: (File) -> Unit) {
    Input(
        type = InputType.File,
        attrs = {
            classes("qk-adm-input-file")
            attr("accept", "image/png,image/jpeg")
            onChange { event ->
                val input = event.target as? HTMLInputElement
                input?.files?.item(0)?.let(onFile)
                input?.value = ""
            }
        },
    )
}

/** Dimmed overlay with a spinner shown while a media file is uploading. */
@Composable
private fun UploadSpinner() {
    Box(
        Modifier.fillMaxSize().position(Position.Absolute).top(0.px).left(0.px)
            .backgroundColor(Color.rgba(15, 20, 17, 0.42f)),
        contentAlignment = Alignment.Center,
    ) {
        Box(Modifier.attrsModifier { classes("qk-spin") })
    }
}
